import { ComponentType, isValidElement, lazy, ReactNode, Suspense } from 'react'
import { AccountTypeGuard } from '@/components/auth/AccountTypeGuard'
import { FeatureFlagGuard } from '@/components/auth/FeatureFlagGuard'
import { SubscriptionFeatureGuard } from '@/components/auth/SubscriptionFeatureGuard'
import { OrgRouteGuard } from '@/components/organization/OrgRouteGuard'
import { FeatureErrorBoundary } from '@/components/shared/FeatureErrorBoundary'
import { RequireMemberAccess } from '@/components/shared/RequireMemberAccess'
import { legacyFinancialsRedirect } from '@/pages/financials/config'
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import { AccountType } from '@scrambl/shared'

import { ProtectedLayout } from '../components'
import { AppRouteObject } from '../types'

// Cache route-module imports so repeated visits don't re-trigger the dynamic import
// promise within the same window. We intentionally do not start these imports at
// bootstrap because every desktop popup/new window gets its own JS context, and
// eager route imports substantially increase first paint time.
const eagerCache = new Map<string, Promise<Record<string, unknown>>>()

function eagerImport(
  importFn: () => Promise<Record<string, unknown>>,
): () => Promise<Record<string, unknown>> {
  const key = importFn.toString()
  return () => {
    const cachedImport = eagerCache.get(key)
    if (cachedImport) {
      return cachedImport
    }

    const nextImport = importFn()
    eagerCache.set(key, nextImport)
    return nextImport
  }
}

function LegacyMemberRatesAndCompensationRedirect() {
  const location = useLocation()
  const { orgSlug = '', memberId = '' } = useParams<{
    orgSlug: string
    memberId: string
  }>()
  return (
    <Navigate
      to={`/${orgSlug}/team/people/${memberId}/pay${location.hash || ''}`}
      replace
    />
  )
}

/**
 * /threads/:threadId → the Threads page with that thread open. Record links,
 * module badges and Benny's cards build the path form (getModuleNavigationPath)
 * but the page selects a thread by ?thread=, so the path 404'd.
 */
function ThreadPathRedirect() {
  const { orgSlug = '', threadId = '' } = useParams<{
    orgSlug: string
    threadId: string
  }>()
  return (
    <Navigate
      to={`/${orgSlug}/threads?thread=${encodeURIComponent(threadId)}`}
      replace
    />
  )
}

/** /members was a placeholder page; members are managed in Settings. */
function LegacyMembersRedirect() {
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  return <Navigate to={`/${orgSlug}/settings/administration/members`} replace />
}

/**
 * /subscription[/*] → /admin/subscription[/*]. Absolute on purpose: a relative
 * `admin/subscription` resolved under /subscription and re-matched the splat,
 * growing the URL forever.
 */
function LegacySubscriptionRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  const suffix = location.pathname.split('/subscription')[1] || ''

  return (
    <Navigate
      to={`/${orgSlug}/admin/subscription${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

function LegacyMyExpensesRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  const suffix = location.pathname.split('/my-expenses')[1] || ''

  return (
    <Navigate
      to={`/${orgSlug}/my-time/expenses${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

/**
 * Every module settings page has two addresses that render the same thing:
 * the module's own <module>/settings route (its Settings tab, with the
 * module header and tab bar around it) and the Settings shell at
 * /settings/modules/:moduleKey (pages/settings-hub/lib/shell-routes.ts,
 * which hosts the very same component). Both keep working; `?section=`
 * deep links open the right pane on either.
 */

const OrganizationSettingsPage = lazy(() => import('@/pages/admin/settings'))

/**
 * The Settings hub root. Plain /settings opens the user's own account
 * settings (the Personal stack); /settings?section=<id> hosts Organization
 * Settings, which is how every link to that page addresses it.
 */
function SettingsRootRoute() {
  const [searchParams] = useSearchParams()
  if (!searchParams.has('section')) {
    return <Navigate to="personal" replace />
  }
  return (
    <Suspense fallback={null}>
      <OrganizationSettingsPage />
    </Suspense>
  )
}

/** /user-settings#tab → /settings/personal#tab (the Personal stack of the shell). */
function LegacyUserSettingsRedirect() {
  const { orgSlug } = useParams()
  const location = useLocation()
  return (
    <Navigate
      to={{
        pathname: `/${orgSlug}/settings/personal`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  )
}

/**
 * Extra aliases for a module's settings path (/expenses/all/settings,
 * /tasks/settings) land on that module's own settings route, which renders
 * the same page the Settings hub hosts. The query string and hash carry
 * across so `?section=` deep links still open the right pane.
 */
function ModuleSettingsAliasRedirect({ to }: { to: string }) {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  return (
    <Navigate
      to={`/${orgSlug}${to}${location.search}${location.hash}`}
      replace
    />
  )
}

function LegacyMyTasksRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()
  const suffix = location.pathname.endsWith('/reviews')
    ? '/reviews'
    : location.pathname.endsWith('/settings')
      ? '/settings'
      : '/mine'

  return (
    <Navigate
      to={`/${orgSlug}/tasks${suffix}${location.search}${location.hash}`}
      replace
    />
  )
}

function LegacyMyNotesRedirect() {
  const location = useLocation()
  const { orgSlug = '', noteId } = useParams<{
    orgSlug: string
    noteId?: string
  }>()
  const isCollectionsRoute = location.pathname.endsWith('/collections')
  const suffix = noteId ? `/${encodeURIComponent(noteId)}` : ''
  const nextSearch = new URLSearchParams(location.search)
  if (isCollectionsRoute) nextSearch.set('type', 'collections')
  const search = nextSearch.toString()

  return (
    <Navigate
      to={`/${orgSlug}/notes/mine${suffix}${search ? `?${search}` : ''}${location.hash}`}
      replace
    />
  )
}

// Project templates are configuration (beside cycle templates), so they moved
// from a top-level Projects tab into Projects Settings.
function LegacyProjectTemplatesRedirect() {
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  return (
    <Navigate to={`/${orgSlug}/projects/settings?section=templates`} replace />
  )
}

// Retainer plans are a contract catalog (templates), so they moved from a
// top-level Contracts tab into Contracts Settings beside contract types.
function LegacyRetainerPlansRedirect() {
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  return (
    <Navigate
      to={`/${orgSlug}/contracts/settings?section=retainer-plans`}
      replace
    />
  )
}

function LegacyCommercialOpsRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  return (
    <Navigate
      to={`/${orgSlug}/contracts/billing${location.search}${location.hash}`}
      replace
    />
  )
}

// Old per-status invoice tab routes (/invoices/draft etc.) map onto the
// single filter-bar list: /invoices?status=DRAFT. Settings keeps its route.
function LegacyInvoiceTabRedirect() {
  const location = useLocation()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  const segment = location.pathname.split('/').filter(Boolean).pop()
  const statusBySegment: Record<string, string> = {
    draft: 'DRAFT',
    sent: 'SENT',
    paid: 'PAID',
    overdue: 'OVERDUE',
  }
  const status = segment ? statusBySegment[segment] : undefined
  const search = new URLSearchParams(location.search)
  if (status) search.set('status', status)
  const query = search.toString()

  return (
    <Navigate
      to={`/${orgSlug}/invoices${query ? `?${query}` : ''}${location.hash}`}
      replace
    />
  )
}

function LegacyHelpRedirect() {
  const location = useLocation()
  const { categorySlug, helpId } = useParams<{
    categorySlug?: string
    helpId?: string
  }>()

  const suffix = (() => {
    if (helpId) return `/articles/${encodeURIComponent(helpId)}`
    if (categorySlug) return `/categories/${encodeURIComponent(categorySlug)}`
    if (location.pathname.endsWith('/help/search')) return '/search'
    return ''
  })()

  return (
    <Navigate to={`/help${suffix}${location.search}${location.hash}`} replace />
  )
}

/**
 * Helper for inline `lazy:` routes that map import → { Component }.
 * Imports stay lazy, but repeat loads reuse the same promise.
 *
 * Usage: `lazy: eagerLazy(() => import('@/pages/foo'), m => m.FooComponent)`
 */
function eagerLazy<T>(
  importFn: () => Promise<T>,
  map: (m: T) => { Component: ComponentType<unknown> },
): () => Promise<{ Component: ComponentType<unknown> }> {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return () => load().then(m => map(m as T))
}

// Helper to create lazy routes with per-window import promise caching
const lazyLoad = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const Component = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      return { Component }
    },
  }
}

// Helper for member-only lazy routes — redirects external users to /today.
// Keep this limited to personal member surfaces such as My Time and My Expenses.
// Time tracking / expenses are business surfaces (TIME_TRACKING off for
// personal accounts), so this also gates to ORGANIZATION — a personal org owner
// passes the member/permission checks but must not reach these routes.
const memberOnlyLazy = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      return {
        Component: () => (
          <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
            <RequireMemberAccess>
              <PageComponent />
            </RequireMemberAccess>
          </AccountTypeGuard>
        ),
      }
    },
  }
}

// Helper for subscription-guarded lazy routes with per-window import promise caching
const lazyLoadWithGuard = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  /**
   * Subscription feature the page needs. Omit for pages with no plan gate:
   * an undefined key used to reach the guard as checkFeature(undefined) =
   * false, so My agents and every agent detail page showed the Pro upgrade
   * wall to everyone.
   */
  featureKey: string | undefined,
  namedExport?: string,
  options?: { orgOnly?: boolean },
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      const guarded = featureKey ? (
        <SubscriptionFeatureGuard featureKey={featureKey}>
          <PageComponent />
        </SubscriptionFeatureGuard>
      ) : (
        <PageComponent />
      )
      return {
        Component: () =>
          options?.orgOnly ? (
            <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
              {guarded}
            </AccountTypeGuard>
          ) : (
            guarded
          ),
      }
    },
  }
}

/**
 * Expenses pages: the sidebar shows them to organizations on a plan with
 * `expenses`, but the routes were unguarded, so a direct link opened them on
 * other plans — and, since plan checks are inert for personal accounts, in a
 * personal space too (CLAUDE.md: business routes need an org-only guard).
 */
const expensesRoute = () =>
  lazyLoadWithGuard(() => import('@/pages/expenses'), 'expenses', undefined, {
    orgOnly: true,
  })

const SUBSCRIPTION_FEATURE_BY_FLAG: Partial<
  Record<FeatureFlagKeyType, string>
> = {
  TICKETS: 'tickets',
  DEALS: 'deals',
  ENTITIES: 'clients',
  INVOICES: 'invoices',
  CONTRACTS: 'contracts',
  QUOTES: 'quotes',
  PRODUCTS: 'products',
  FORMS: 'forms',
  EVENTS: 'events',
  SUPPLIERS: 'suppliers',
  FINANCIALS: 'profitability',
  SCHEDULING: 'scheduling',
  KNOWLEDGE_BASE: 'knowledge_base',
  MARKETING: 'marketing',
  LIVE_CHAT: 'live_chat',
  CALLING: 'calling',
  // The ADVANCED_REPORTS *flag* gates the Analytics module, whose
  // subscription key is `analytics` — the same key AnalyticsController
  // enforces via @RequireFeature and the one MODULE_TYPE_SUBSCRIPTION_FEATURE
  // records for ModuleTypeEnum.ANALYTICS. The similarly-named
  // `advanced_reports` subscription feature is a separate Business-only perk
  // (false on free/core/pro), so pointing at it here would lock Core and Pro
  // orgs out of a module their API serves.
  ADVANCED_REPORTS: 'analytics',
  SEQUENCES: 'sequences',
  ENRICHMENT: 'enrichment',
  WORKFLOWS: 'workflows',
  DISCUSSIONS: 'discussions',
}

// Helper for feature-flag-guarded lazy routes with per-window import promise caching
const lazyLoadWithFeatureFlag = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  flagKey: FeatureFlagKeyType,
  namedExport?: string,
  options?: { orgOnly?: boolean; personalOnly?: boolean },
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      const subscriptionFeature = SUBSCRIPTION_FEATURE_BY_FLAG[flagKey]
      return {
        Component: () => {
          const page = subscriptionFeature ? (
            <SubscriptionFeatureGuard featureKey={subscriptionFeature}>
              <PageComponent />
            </SubscriptionFeatureGuard>
          ) : (
            <PageComponent />
          )

          const flagged = (
            <FeatureFlagGuard flagKey={flagKey}>{page}</FeatureFlagGuard>
          )

          if (options?.orgOnly)
            return (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                {flagged}
              </AccountTypeGuard>
            )
          if (options?.personalOnly)
            return (
              <AccountTypeGuard allowed={[AccountType.PERSONAL]}>
                {flagged}
              </AccountTypeGuard>
            )
          return flagged
        },
      }
    },
  }
}

const lazyLoadWithFeatureFlags = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  flagKeys: FeatureFlagKeyType[],
  namedExport?: string,
) => {
  const load = eagerImport(importFn as () => Promise<Record<string, unknown>>)
  return {
    lazy: async () => {
      const module = await load()
      const PageComponent = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default

      return {
        Component: () =>
          flagKeys.reduceRight(
            (child, flagKey) => {
              const subscriptionFeature = SUBSCRIPTION_FEATURE_BY_FLAG[flagKey]
              const guardedChild = subscriptionFeature ? (
                <SubscriptionFeatureGuard featureKey={subscriptionFeature}>
                  {child}
                </SubscriptionFeatureGuard>
              ) : (
                child
              )

              return (
                <FeatureFlagGuard flagKey={flagKey}>
                  {guardedChild}
                </FeatureFlagGuard>
              )
            },
            (<PageComponent />) as ReactNode,
          ),
      }
    },
  }
}

const tasksErrorElement = <FeatureErrorBoundary featureName="Tasks" />
const invoicesErrorElement = <FeatureErrorBoundary featureName="Invoices" />
const notesErrorElement = <FeatureErrorBoundary featureName="Notes" />
const documentsErrorElement = <FeatureErrorBoundary featureName="Documents" />
const threadsErrorElement = <FeatureErrorBoundary featureName="Threads" />
const ticketsErrorElement = <FeatureErrorBoundary featureName="Tickets" />
const RedirectInboxToNotes = () => {
  const { noteId, orgSlug } = useParams()

  if (!noteId || !orgSlug) {
    return <Navigate to="../notes" replace />
  }

  return <Navigate to={`/${orgSlug}/notes/${noteId}`} replace />
}

// /clients/* → /entities/*: the sidebar calls the module Clients, so the
// guessable URL should land there rather than on "Page not found".
const RedirectClientsToEntities = () => {
  const { orgSlug, '*': rest } = useParams()
  const { search, hash } = useLocation()
  const path = rest ? `entities/${rest}` : 'entities'
  return (
    <Navigate
      to={`${orgSlug ? `/${orgSlug}/${path}` : `../${path}`}${search}${hash}`}
      replace
    />
  )
}

// Redirects legacy /meetings → /notes/meetings (Meetings is a Notes sub-tab)
const RedirectToRecordings = () => {
  const { orgSlug } = useParams()
  return (
    <Navigate
      to={orgSlug ? `/${orgSlug}/notes/recordings` : '../notes/recordings'}
      replace
    />
  )
}

// Redirects /team/planning/schedule/:id → /team/people/:id/schedule (if id), else /team/planner
const PlanningScheduleRedirect = () => {
  const { id } = useParams<{ id?: string }>()
  return id ? (
    <Navigate to={`../team/people/${id}/schedule`} replace />
  ) : (
    <Navigate to="../team/planner" replace />
  )
}

// Redirects legacy /team/planning paths to /team/planner, keeping the query
// string so scope params (projectId, entityId, ...) survive the hop.
const PlannerRedirect = () => {
  const location = useLocation()
  return (
    <Navigate
      to={{ pathname: '../team/planner', search: location.search }}
      replace
    />
  )
}

const LegacyTeamRoleRedirect = () => {
  const { roleId } = useParams<{ roleId?: string }>()
  return roleId ? (
    <Navigate to={`../financials/setup/rates/roles/${roleId}`} replace />
  ) : (
    <Navigate to="../financials/setup/rates/roles" replace />
  )
}

// Team's money tabs (financials, forecast, scenarios, rates, earnings) moved
// to the Profitability module at /financials. Rewrite any legacy /team money
// URL onto the new root, preserving deeper segments and the query string, so
// bookmarks, server-generated hrefs, and help links keep working.
const LegacyTeamMoneyRedirect = () => {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)
  // segments: [orgSlug, 'team', money, ...rest]
  const money = segments[2]
  const rest = segments.slice(3)
  let target: string
  if (money === 'financials') {
    // /team/financials[/overview|performance|forecast] -> /financials/...
    // (/team/financials/scenarios predates the scenarios promotion)
    target = ['financials', ...rest].join('/')
  } else if (money === 'forecast') {
    target = 'financials/forecast'
  } else if (money === 'compensation') {
    target = 'financials/rates'
  } else {
    // scenarios | rates | earnings, with subroutes intact
    target = ['financials', money, ...rest].join('/')
  }
  // The ten-tab module URLs then fold into the four-tab layout.
  const canonical = legacyFinancialsRedirect(
    `/${segments[0]}/${target}`,
    location.search,
  )
  return canonical ? (
    <Navigate
      to={{
        pathname: `..${canonical.pathname}`,
        search: canonical.search,
        hash: canonical.hash,
      }}
      replace
    />
  ) : (
    <Navigate
      to={{ pathname: `../${target}`, search: location.search }}
      replace
    />
  )
}

/**
 * The Profitability module's ten-tab URLs (2026-09-16 consolidation) →
 * their canonical homes under Overview / Forecast / Billing / Setup, with
 * deeper segments and the query string intact.
 */
const LegacyFinancialsRedirect = () => {
  const location = useLocation()
  const canonical = legacyFinancialsRedirect(location.pathname, location.search)
  return canonical ? (
    <Navigate
      to={{
        pathname: `..${canonical.pathname}`,
        search: canonical.search,
        hash: canonical.hash,
      }}
      replace
    />
  ) : (
    <Navigate
      to={{ pathname: '../financials', search: location.search }}
      replace
    />
  )
}

const financialsRoute = (path: string, title: string): AppRouteObject => ({
  path,
  ...lazyLoadWithFeatureFlag(
    () => import('@/pages/financials'),
    'FINANCIALS',
    undefined,
    { orgOnly: true },
  ),
  title,
})

// Organization routes configuration
export const organizationRoutes: AppRouteObject[] = [
  {
    path: '/:orgSlug',
    element: <ProtectedLayout />,
    children: [
      {
        path: 'help',
        element: <LegacyHelpRedirect />,
        title: 'Help Center',
      },
      {
        path: 'help/search',
        element: <LegacyHelpRedirect />,
        title: 'Search help',
      },
      {
        path: 'help/categories/:categorySlug',
        element: <LegacyHelpRedirect />,
        title: 'Help category',
      },
      {
        path: 'help/articles/:helpId',
        element: <LegacyHelpRedirect />,
        title: 'Help article',
      },
      {
        element: <OrgRouteGuard />,
        children: [
          // Default redirect
          {
            index: true,
            element: <Navigate to="today" replace />,
          },
          // Simple Mode's workspace as a first-class page: conversations +
          // adaptive canvas, launched from the Today hero. Flag-gated.
          {
            path: 'conversations',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/simple'),
              'SIMPLE_MODE',
              undefined,
              { orgOnly: true },
            ),
            title: 'Conversations',
          },
          // Organization Today
          {
            path: 'today',
            ...lazyLoad(() => import('@/pages/today')),
            title: 'Today',
          },
          // Meal planner (personal spaces; MEAL_PLANNER flag is OFF for
          // business). The router is the third sanctioned personal/work seam
          // crossing — see the eslint no-restricted-imports overrides.
          {
            path: 'meals',
            ...lazyLoadWithFeatureFlag(
              () => import('@/personal/meals/MealPlannerPage'),
              'MEAL_PLANNER',
              undefined,
              // The flag alone let a work org with it switched on open a page
              // whose recipe collection only personal spaces seed.
              { personalOnly: true },
            ),
            title: 'Meals',
          },
          // Guided Setup — resumable onboarding flow (work accounts, admins)
          {
            path: 'getting-started',
            ...lazyLoad(() => import('@/pages/getting-started')),
            title: 'Getting started',
          },
          // Search
          {
            path: 'search',
            ...lazyLoad(() => import('@/pages/search')),
            title: 'Search',
          },
          // My Organization (intranet) routes
          {
            path: 'my-organization',
            ...lazyLoad(() => import('@/pages/my-organization/layout')),
            title: 'My organization',
            children: [
              {
                index: true,
                ...lazyLoad(() => import('@/pages/my-organization')),
                title: 'My organization',
              },
              {
                path: 'overview/:pageSlug',
                ...lazyLoad(() => import('@/pages/my-organization')),
                title: 'My organization',
              },
              {
                path: 'org-chart',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/org-chart'),
                  m => ({ Component: m.OrganizationOrgChartRoute }),
                ),
                title: 'Org chart',
              },
              {
                path: 'projects',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/projects'),
                  m => ({ Component: m.OrganizationProjectsRoute }),
                ),
                title: 'Organization projects',
              },
              {
                path: 'documents',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/documents'),
                  m => ({ Component: m.OrganizationDocumentsRoute }),
                ),
                title: 'Organization documents',
              },
              {
                path: 'discussions',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/discussions'),
                  m => ({ Component: m.OrganizationDiscussionsRoute }),
                ),
                title: 'Organization discussions',
              },
              {
                path: 'notes',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/notes'),
                  m => ({ Component: m.OrganizationNotesRoute }),
                ),
                title: 'Organization notes',
              },
              {
                path: 'tickets',
                lazy: eagerLazy(
                  () => import('@/pages/my-organization/tabs/tickets'),
                  m => ({ Component: m.OrganizationTicketsRoute }),
                ),
                title: 'Organization tickets',
              },
              {
                path: ':pageSlug',
                ...lazyLoad(() => import('@/pages/my-organization/page')),
                title: 'Organization page',
              },
            ],
          },
          // My Tasks route (personal tasks view) — open to external users so
          // they can see tasks they're assigned to in this host org
          {
            path: 'my-tasks',
            element: <LegacyMyTasksRedirect />,
            title: 'My tasks',
            errorElement: tasksErrorElement,
          },
          {
            path: 'my-tasks/reviews',
            element: <LegacyMyTasksRedirect />,
            title: 'My tasks reviews',
            errorElement: tasksErrorElement,
          },
          {
            path: 'my-tasks/settings',
            element: <LegacyMyTasksRedirect />,
            title: 'My tasks settings',
            errorElement: tasksErrorElement,
          },
          // My Notes route
          {
            path: 'my-notes',
            element: <LegacyMyNotesRedirect />,
            title: 'My notes',
            errorElement: notesErrorElement,
          },
          // Collections tab (same page component; static segment outranks :noteId)
          {
            path: 'my-notes/collections',
            element: <LegacyMyNotesRedirect />,
            title: 'My notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'my-notes/:noteId',
            element: <LegacyMyNotesRedirect />,
            title: 'Note',
            errorElement: notesErrorElement,
          },
          {
            path: 'time-exports/:jobId',
            ...lazyLoad(() => import('@/pages/time/export-status')),
            title: 'Time report export',
          },
          // My Time route (personal time tracking)
          {
            path: 'my-time',
            ...memberOnlyLazy(() => import('@/pages/my-time')),
            title: 'My time',
            children: [
              // Canonical paths (Plan 10 consolidation)
              {
                path: 'log',
                lazy: eagerLazy(
                  () => import('@/pages/time/tabs/current-tab'),
                  m => ({ Component: m.CurrentTab }),
                ),
                title: 'Log time',
              },
              {
                path: 'timesheets',
                lazy: eagerLazy(
                  () => import('@/pages/time/tabs/history'),
                  m => ({ Component: m.HistoryTab }),
                ),
                title: 'My timesheets',
              },
              // Legacy paths → redirect to canonical (redirects stay live ≥ 2026-10-21)
              {
                path: 'current',
                element: <Navigate to="../log" replace />,
              },
              {
                path: 'history',
                element: <Navigate to="../timesheets" replace />,
              },
              {
                path: 'availability',
                lazy: eagerLazy(
                  () => import('@/pages/workspace/tabs/availability'),
                  m => ({ Component: m.default }),
                ),
                title: 'Availability',
              },
              {
                path: 'time-off',
                lazy: eagerLazy(
                  () => import('@/pages/workspace/tabs/time-off'),
                  m => ({ Component: m.default }),
                ),
                title: 'Time off',
              },
              {
                path: 'expenses',
                children: [
                  {
                    index: true,
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My expenses',
                  },
                  {
                    path: 'reimbursements',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My expense reimbursements',
                  },
                  {
                    path: 'reimbursements/:expenseId/*',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My expense reimbursement details',
                  },
                  {
                    path: ':expenseId/*',
                    lazy: eagerLazy(
                      () => import('@/pages/my-time/tabs/expenses'),
                      m => ({ Component: m.default }),
                    ),
                    title: 'My expense details',
                  },
                ],
              },
              {
                path: 'contractor-invoices',
                lazy: eagerLazy(
                  () => import('@/pages/my-time/tabs/invoices'),
                  m => ({ Component: m.default }),
                ),
                title: 'Contractor invoices',
              },
              // Legacy invoices path
              {
                path: 'invoices',
                element: <Navigate to="../contractor-invoices" replace />,
              },
            ],
          },
          // Timesheets — consolidated surface for managers/admins
          {
            path: 'timesheets',
            // The sidebar's TIME_TRACKING gate: the page only checks approve /
            // manage permissions, which a personal space's owner always has.
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/timesheets'),
              'TIME_TRACKING',
            ),
            title: 'Timesheets',
            children: [
              {
                index: true,
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/approvals'),
                  m => ({ Component: m.ApprovalsTab }),
                ),
                title: 'All timesheets',
              },
              {
                path: 'reports',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/reports'),
                  m => ({ Component: m.TimesheetsReportsTab }),
                ),
                title: 'Reports',
              },
              {
                path: 'invoices',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/invoices'),
                  m => ({ Component: m.TimesheetsInvoicesTab }),
                ),
                title: 'Invoices',
              },
              {
                path: 'settings',
                lazy: eagerLazy(
                  () => import('@/pages/timesheets/tabs/settings'),
                  m => ({ Component: m.TimesheetsSettingsTab }),
                ),
                title: 'Settings',
              },
            ],
          },
          // Entities routes
          {
            path: 'entities/workflows',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entity workflows',
          },
          {
            path: 'entities/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entity settings',
          },
          {
            path: 'entities/health-triage',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/entities'),
              ['ENTITIES', 'CLIENT_HEALTH'],
            ),
            title: 'Entity health triage',
          },
          {
            path: 'entities/:entityId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities/entity-detail'),
              'ENTITIES',
              'EntityDetail',
            ),
            title: 'Entity details',
          },
          {
            path: 'entities',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/entities'),
              'ENTITIES',
            ),
            title: 'Entities',
          },
          {
            path: 'clients/*',
            element: <RedirectClientsToEntities />,
          },
          // Collections routes (requires subscription feature)
          {
            path: 'collections/:slug/settings',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/[slug]'),
              'collections',
            ),
            title: 'Collection settings',
          },
          {
            path: 'collections/:slug/:entryId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/entry-detail'),
              'collections',
            ),
            title: 'Collection entry',
          },
          {
            path: 'collections/:slug',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections/[slug]'),
              'collections',
            ),
            title: 'Collection details',
          },
          {
            path: 'collections',
            ...lazyLoadWithGuard(
              () => import('@/pages/collections'),
              'collections',
            ),
            title: 'Collections',
          },
          // Projects routes
          {
            path: 'projects/:projectId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects/project-detail'),
              'PROJECTS',
              'ProjectDetail',
            ),
            title: 'Project details',
          },
          {
            path: 'projects/roadmap',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Roadmap',
          },
          {
            path: 'projects/templates',
            element: <LegacyProjectTemplatesRedirect />,
            title: 'Project templates',
          },
          {
            path: 'projects/reports/velocity',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project velocity report',
          },
          {
            path: 'projects/reports',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project reports',
          },
          {
            path: 'projects/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Project settings',
          },
          {
            path: 'projects',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/projects'),
              'PROJECTS',
              'ProjectsPage',
            ),
            title: 'Projects',
          },
          // Tasks routes (All Tasks module - My Tasks is now at /my-tasks)
          {
            path: 'tasks/mine',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'My work',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/mine/settings',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'My work settings',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/reviews',
            ...lazyLoad(() => import('@/pages/tasks')),
            title: 'Task reviews',
            errorElement: tasksErrorElement,
          },
          {
            // My Work settings is the canonical page; /tasks/settings is an
            // alias that predates the /tasks/mine split.
            path: 'tasks/settings',
            element: <ModuleSettingsAliasRedirect to="/tasks/mine/settings" />,
            title: 'Task settings',
          },
          {
            // Registered before tasks/:taskIdentifier so "workstream" is not
            // captured as a task identifier. Workstream is a tab inside the
            // Tasks module but keeps its own WORKSTREAM flag gating.
            path: 'tasks/workstream/:workspaceId?',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tasks'),
              'WORKSTREAM',
            ),
            title: 'Workstream',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks/:taskIdentifier',
            lazy: eagerLazy(
              () => import('@/pages/tasks/task-detail'),
              m => ({ Component: m.TaskDetail }),
            ),
            title: 'Task details',
            errorElement: tasksErrorElement,
          },
          {
            path: 'tasks',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/tasks'), 'TASKS'),
            title: 'All tasks',
            errorElement: tasksErrorElement,
          },
          {
            path: 'focus',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/focus'),
              'FOCUS_MODE',
            ),
            title: 'Focus',
            errorElement: tasksErrorElement,
          },
          {
            // Work-account streaks, badges and weekly goals.
            path: 'achievements',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/engagement'),
              'GAMIFICATION',
              undefined,
              { orgOnly: true },
            ),
            title: 'Achievements',
          },
          {
            path: 'tickets/:ticketIdentifier',
            lazy: eagerLazy(
              () => import('@/pages/tickets/ticket-detail'),
              m => ({ Component: m.TicketDetail }),
            ),
            title: 'Ticket details',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Tickets',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket settings',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/roadmap',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket roadmap',
            errorElement: ticketsErrorElement,
          },
          {
            path: 'tickets/insights',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/tickets'),
              'TICKETS',
            ),
            title: 'Ticket insights',
            errorElement: ticketsErrorElement,
          },
          // Calendar routes
          {
            path: 'calendar',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar',
          },
          {
            path: 'calendar/month',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Month',
          },
          {
            path: 'calendar/week',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Week',
          },
          {
            path: 'calendar/day',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Day',
          },
          {
            path: 'calendar/agenda',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar - Agenda',
          },
          {
            path: 'calendar/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/calendar'),
              'CALENDAR',
            ),
            title: 'Calendar settings',
          },
          // Timesheet approval detail page
          {
            path: 'timesheets/approvals/:userId',
            ...lazyLoad(
              () => import('@/pages/timesheets/timesheet-detail'),
              'ApprovalDetail',
            ),
            title: 'Timesheet approval detail',
          },
          // Legacy /time/* redirects (grace period: ≥ 2026-10-21)
          {
            path: 'time/log',
            element: <Navigate to="../my-time/log" replace />,
          },
          {
            path: 'time/approvals',
            element: <Navigate to="../timesheets" replace />,
          },
          {
            path: 'time/history',
            element: <Navigate to="../my-time/timesheets" replace />,
          },
          {
            path: 'time/reports',
            element: <Navigate to="../timesheets/reports" replace />,
          },
          {
            path: 'time',
            element: <Navigate to="../my-time" replace />,
          },
          {
            path: 'time/*',
            element: <Navigate to="../my-time" replace />,
          },
          // Contacts routes
          {
            path: 'contacts/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts'),
              'CONTACTS',
            ),
            title: 'Contact settings',
          },
          {
            path: 'contacts/data-quality',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/contacts'),
              ['CONTACTS', 'ENRICHMENT'],
            ),
            title: 'Data quality',
          },
          {
            path: 'contacts/:contactId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts/contact-detail'),
              'CONTACTS',
              'ContactDetail',
            ),
            title: 'Contact details',
          },
          {
            path: 'contacts',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contacts'),
              'CONTACTS',
            ),
            title: 'Contacts',
          },
          {
            path: 'deals/settings',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deal settings',
          },
          {
            path: 'deals/command-center',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/deals'),
              ['DEALS', 'CLIENT_HEALTH'],
            ),
            title: 'Revenue command center',
          },
          {
            path: 'deals/forecast',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deal forecast',
          },
          {
            path: 'deals/:dealId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/deals/deal-detail'),
              'DEALS',
              'DealDetail',
            ),
            title: 'Deal details',
          },
          {
            path: 'deals',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/deals'), 'DEALS'),
            title: 'Deals',
          },
          // Marketing routes
          {
            path: 'marketing',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing'),
              'MARKETING',
            ),
            title: 'Marketing',
          },
          {
            path: 'marketing/campaigns/:campaignId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing/campaign-detail'),
              'MARKETING',
              'CampaignDetail',
            ),
            title: 'Campaign',
          },
          {
            path: 'marketing/journeys/:journeyId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/marketing/journey-builder'),
              'MARKETING',
              'JourneyBuilder',
            ),
            title: 'Journey',
          },
          {
            path: 'scheduling',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/scheduling'),
              'SCHEDULING',
            ),
            title: 'Scheduling',
          },
          {
            path: 'communications/sequences/:sequenceId',
            ...lazyLoadWithFeatureFlags(
              () => import('@/pages/communications/sequence-detail'),
              ['CALLING', 'SEQUENCES'],
              'SequenceDetail',
            ),
            title: 'Sequence',
          },
          {
            path: 'communications',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/communications'),
              'CALLING',
            ),
            title: 'Communications',
          },
          // Quotes routes
          {
            path: 'quotes/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes'),
              'QUOTES',
            ),
            title: 'Quote settings',
          },
          {
            path: 'quotes/:quoteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes/quote-detail'),
              'QUOTES',
              'QuoteDetail',
            ),
            title: 'Quote',
          },
          {
            path: 'quotes',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/quotes'),
              'QUOTES',
            ),
            title: 'Quotes',
          },
          // Products (price book + stock) — its own module, used by quotes +
          // invoices; also hosts the absorbed Inventory tabs under /products/*
          {
            // Registered before products/* so the splat never captures it.
            path: 'products/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/products'),
              'PRODUCTS',
            ),
            title: 'Product settings',
          },
          {
            path: 'products/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/products'),
              'PRODUCTS',
            ),
            title: 'Products',
          },
          // Knowledge base routes. Tabs are path segments; the module page
          // resolves the active tab from the pathname and redirects legacy
          // `?tab=` links.
          {
            path: 'knowledge-base',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge base',
          },
          {
            path: 'knowledge-base/content',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge base',
          },
          {
            path: 'knowledge-base/templates',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge base',
          },
          {
            path: 'knowledge-base/recordings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge base',
          },
          {
            path: 'knowledge-base/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base'),
              'KNOWLEDGE_BASE',
            ),
            title: 'Knowledge base settings',
          },
          {
            // Static `recordings` segment registered before the param routes
            // below so it can never be captured as an :articleId.
            path: 'knowledge-base/recordings/:id',
            ...memberOnlyLazy(
              () => import('@/pages/knowledge-base/recordings/[id]'),
            ),
            title: 'SOP recording',
          },
          {
            path: 'knowledge-base/read/:articleId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base/article-reader'),
              'KNOWLEDGE_BASE',
              'KbArticleReader',
            ),
            title: 'Knowledge base article',
          },
          {
            path: 'knowledge-base/articles/:articleId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/knowledge-base/article-editor'),
              'KNOWLEDGE_BASE',
              'KbArticleEditor',
            ),
            title: 'Article',
          },
          {
            path: 'live-chat',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/live-chat'),
              'LIVE_CHAT',
            ),
            title: 'Live chat',
          },
          // Billing routes
          {
            path: 'invoices',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices'),
              'INVOICES',
            ),
            title: 'Invoices',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices'),
              'INVOICES',
            ),
            title: 'Invoice settings',
            errorElement: invoicesErrorElement,
          },
          // Legacy per-status tab routes redirect into the filter bar
          {
            path: 'invoices/all',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/draft',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/sent',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/paid',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/overdue',
            element: <LegacyInvoiceTabRedirect />,
          },
          {
            path: 'invoices/create',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/editor/invoice-editor'),
              'INVOICES',
            ),
            title: 'New invoice',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/:id/edit',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/editor/invoice-editor'),
              'INVOICES',
            ),
            title: 'Edit invoice',
            errorElement: invoicesErrorElement,
          },
          {
            path: 'invoices/:id',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/invoices/invoice-detail'),
              'INVOICES',
            ),
            title: 'Invoice details',
            errorElement: invoicesErrorElement,
          },
          // Legacy: My Expenses moved under My Time
          {
            path: 'my-expenses/*',
            element: <LegacyMyExpensesRedirect />,
          },
          {
            path: 'expenses',
            children: [
              {
                index: true,
                ...expensesRoute(),
                title: 'Expenses',
              },
              {
                path: 'inbox',
                ...expensesRoute(),
                title: 'Expense inbox',
              },
              {
                path: 'all',
                ...expensesRoute(),
                title: 'Expenses',
              },
              {
                path: 'reimbursements',
                ...expensesRoute(),
                title: 'Expense reimbursements',
              },
              {
                path: 'client-coding',
                ...expensesRoute(),
                title: 'Client coding',
              },
              {
                path: 'settings',
                ...expensesRoute(),
                title: 'Expense settings',
              },
              {
                // Legacy /expenses/all/settings deep links
                path: 'all/settings',
                element: (
                  <ModuleSettingsAliasRedirect to="/expenses/settings" />
                ),
              },
              {
                path: 'inbox/:expenseId/*',
                ...expensesRoute(),
                title: 'Expense inbox detail',
              },
              {
                path: 'all/:expenseId/*',
                ...expensesRoute(),
                title: 'Expense detail',
              },
              {
                path: 'reimbursements/:expenseId/*',
                ...expensesRoute(),
                title: 'Expense reimbursement detail',
              },
              {
                path: ':expenseId/*',
                ...expensesRoute(),
                title: 'Expense details',
              },
            ],
          },
          {
            // Renders as a Contracts module tab (the page routes on the path
            // segment), no longer a standalone page outside the module layout.
            path: 'contracts/retainers',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Retainers',
          },
          {
            path: 'contracts/billing',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Billing',
          },
          {
            // Contracts module tab (SD-123); the page gates it further on the
            // Invoices flag and client-authorization read permission.
            path: 'contracts/authorizations',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Client authorizations',
          },
          {
            path: 'contracts/service-retainers',
            element: <LegacyRetainerPlansRedirect />,
            title: 'Retainer plans',
          },
          {
            path: 'contracts/contract-billing',
            element: <LegacyCommercialOpsRedirect />,
            title: 'Billing',
          },
          {
            path: 'contracts/commercial-ops',
            element: <LegacyCommercialOpsRedirect />,
            title: 'Billing',
          },
          {
            path: 'contracts/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Contract settings',
          },
          {
            path: 'contracts/:contractId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/contract-detail'),
              'CONTRACTS',
              'ContractDetail',
            ),
            title: 'Contract details',
          },
          {
            path: 'contracts',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/contracts/page'),
              'CONTRACTS',
            ),
            title: 'Contracts',
          },
          // Forms routes
          // A form has no bare page; record links, module badges and Benny's
          // cards build /forms/:formId (getModuleNavigationPath) and hit a 404.
          {
            path: 'forms/:formId',
            element: <Navigate to="edit" replace />,
          },
          {
            path: 'forms/:formId/edit',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/edit'),
              'FORMS',
            ),
            title: 'Edit form',
          },
          {
            path: 'forms/:formId/submissions',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/submissions'),
              'FORMS',
            ),
            title: 'Form submissions',
          },
          {
            path: 'forms/:formId/analytics',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/analytics'),
              'FORMS',
            ),
            title: 'Form analytics',
          },
          {
            path: 'forms/:formId/branding',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/branding'),
              'FORMS',
            ),
            title: 'Form branding',
          },
          {
            path: 'forms/:formId/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/forms/[formId]/settings'),
              'FORMS',
            ),
            title: 'Form settings',
          },
          {
            // Forms has no module-level settings page. This path used to
            // render the list silently and was advertised in omni-search, so
            // redirect rather than 404 any lingering bookmarks.
            path: 'forms/settings',
            element: <Navigate to="../forms" replace />,
          },
          {
            path: 'forms',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/forms'), 'FORMS'),
            title: 'Forms',
          },
          // Workflows (requires subscription feature)
          {
            path: 'workflows',
            // The WORKFLOWS flag the API enforces (off for personal spaces,
            // where a plan guard alone is inert) plus the plan gate.
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/workflows/visual'),
              'WORKFLOWS',
            ),
            title: 'Workflows',
          },
          // Analytics routes.
          //
          // Gated by the ADVANCED_REPORTS feature flag (which resolves to the
          // `analytics` subscription feature via SUBSCRIPTION_FEATURE_BY_FLAG)
          // plus orgOnly. A subscription feature alone is not a gate for
          // personal accounts — their subscription layer is inert and returns
          // true for everything — so the flag and the account-type guard are
          // what actually keep this business surface out of personal spaces.
          {
            path: 'analytics',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Analytics',
          },
          {
            path: 'analytics/reports',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Reports',
          },
          {
            path: 'analytics/alerts',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Alerts',
          },
          {
            path: 'analytics/dashboards/:id',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics/DashboardBuilder'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Dashboard',
          },
          {
            path: 'analytics/reports/:id',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics/ReportBuilder'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Report',
          },
          {
            // `:id` is the rule id, or the literal `new` for creation —
            // matching the mobile route contract
            // (`analytics-alert-rule/[ruleId]` with `ruleId === 'new'`).
            path: 'analytics/alerts/rules/:id',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/analytics/AlertRuleBuilder'),
              'ADVANCED_REPORTS',
              undefined,
              { orgOnly: true },
            ),
            title: 'Alert rule',
          },
          // Community routes (requires subscription feature)
          {
            path: 'threads',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/discussions/page'),
              'DISCUSSIONS',
            ),
            title: 'Threads',
            errorElement: threadsErrorElement,
          },
          {
            path: 'threads/:threadId',
            element: <ThreadPathRedirect />,
          },
          // Redirect old discussions path to threads
          {
            path: 'discussions',
            element: <Navigate to="../threads" replace />,
          },
          {
            // Notes has no settings page, but this static route must outrank
            // `notes/:noteId` below — without it the URL loads note detail
            // with noteId="settings", which only reaches the list after a
            // failed fetch.
            path: 'notes/settings',
            element: <Navigate to="../notes" replace />,
          },
          {
            path: 'notes/mine/collections',
            element: <LegacyMyNotesRedirect />,
            title: 'My note collections',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/mine/:noteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/[noteId]'),
              'NOTES',
            ),
            title: 'Note',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/mine',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/my-notes'),
              'NOTES',
            ),
            title: 'My notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/templates',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/templates'),
              'NOTES',
            ),
            title: 'Note templates',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/templates/:templateId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/template-editor'),
              'NOTES',
            ),
            title: 'Edit template',
            errorElement: notesErrorElement,
          },
          {
            // Must precede notes/:noteId so the static segment wins.
            path: 'notes/sop-recordings/:id',
            ...memberOnlyLazy(
              () => import('@/pages/notes/sop-recordings/[id]'),
            ),
            title: 'SOP recording',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/:noteId',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/notes/[noteId]'),
              'NOTES',
            ),
            title: 'Note',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes',
            ...lazyLoadWithFeatureFlag(() => import('@/pages/notes'), 'NOTES'),
            title: 'Notes',
            errorElement: notesErrorElement,
          },
          {
            path: 'notes/recordings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/recordings'),
              'MEETING_NOTES',
            ),
            title: 'Recordings',
            errorElement: notesErrorElement,
          },
          {
            // Legacy URL from when this tab was called "Meetings"
            path: 'notes/meetings',
            element: <RedirectToRecordings />,
          },
          {
            // Legacy URL from when Meetings was a top-level nav item
            path: 'meetings',
            element: <RedirectToRecordings />,
          },
          {
            path: 'documents/:documentId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/documents/[documentId]'),
              'DOCUMENTS',
            ),
            title: 'Document',
            errorElement: documentsErrorElement,
          },
          {
            path: 'documents',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/documents'),
              'DOCUMENTS',
            ),
            title: 'Documents',
            errorElement: documentsErrorElement,
          },
          // Redirect old inbox routes to notes
          {
            path: 'inbox/:noteId',
            element: <RedirectInboxToNotes />,
          },
          {
            path: 'inbox',
            element: <Navigate to="../notes" replace />,
          },
          {
            path: 'events/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events'),
              'EVENTS',
            ),
            title: 'Event settings',
          },
          {
            path: 'events/:eventId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events/event-detail'),
              'EVENTS',
              'EventDetail',
            ),
            title: 'Event details',
          },
          {
            path: 'events',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/events'),
              'EVENTS',
            ),
            title: 'Events',
          },

          // Notifications route
          {
            path: 'notifications',
            ...lazyLoad(() => import('@/pages/notifications')),
            title: 'Notifications',
          },

          // Legacy My Profile routes - redirect to the Personal settings
          {
            path: 'me',
            element: <Navigate to="../settings/personal#account" replace />,
            title: 'My profile',
          },
          {
            path: 'me/skills',
            element: <Navigate to="../settings/personal#skills" replace />,
            title: 'My skills',
          },
          // Legacy redirects - availability and time-off moved to my-time
          {
            path: 'me/availability',
            element: <Navigate to="../my-time/availability" replace />,
          },
          {
            path: 'me/time-off',
            element: <Navigate to="../my-time/time-off" replace />,
          },

          // Account settings moved into the Settings shell's Personal stack
          // (/settings/personal#tab). Keep the old address for links, Benny,
          // and bookmarks; the hash (tab) carries over.
          {
            path: 'user-settings',
            element: <LegacyUserSettingsRedirect />,
            title: 'Account settings',
          },

          // Settings routes. The Settings shell is a layout route: its
          // children render inside the shell's pane (see
          // pages/settings-hub/lib/shell-routes.ts). The old per-module
          // settings routes (projects/settings and the rest) keep working
          // until they are redirected here.
          {
            path: 'settings',
            ...lazyLoad(() => import('@/pages/settings-hub')),
            title: 'Settings',
            children: [
              {
                // Settings root: opens your account settings; with
                // ?section= it hosts Organization Settings instead.
                index: true,
                element: <SettingsRootRoute />,
                title: 'Settings',
              },
              {
                // Personal stack: the account page (global Account tab plus
                // the org-scoped Skills / Notifications / Integrations tabs),
                // switched by #hash from the accordion.
                path: 'personal',
                ...lazyLoad(() => import('@/pages/organization-account')),
                title: 'Account settings',
              },
              {
                path: 'modules',
                lazy: eagerLazy(
                  () =>
                    import('@/pages/settings-hub/_components/SettingsShellPlaceholder'),
                  m => ({ Component: m.SettingsShellPlaceholder }),
                ),
                title: 'Module settings',
              },
              {
                // Hosts one module's settings tab; the host map applies the
                // module's own feature-flag / subscription / account guards.
                path: 'modules/:moduleKey',
                lazy: eagerLazy(
                  () =>
                    import('@/pages/settings-hub/_components/ModuleSettingsHost'),
                  m => ({ Component: m.ModuleSettingsHost }),
                ),
                title: 'Module settings',
              },
              {
                // Administration tab before a page is chosen.
                path: 'administration',
                lazy: eagerLazy(
                  () =>
                    import('@/pages/settings-hub/_components/SettingsShellPlaceholder'),
                  m => ({ Component: m.SettingsShellPlaceholder }),
                ),
                title: 'Administration',
              },
              {
                // Hosts one admin page (Members, Integrations, Audit Trail,
                // Archive, Subscription) and the sub-pages it navigates
                // between; the host map applies the admin route guards. The
                // old admin-mode routes at /admin/* redirect here.
                path: 'administration/:adminKey',
                title: 'Administration',
                children: [
                  {
                    index: true,
                    lazy: eagerLazy(
                      () =>
                        import('@/pages/settings-hub/_components/AdminSettingsHost'),
                      m => ({ Component: m.AdminSettingsHost }),
                    ),
                  },
                  {
                    path: ':subPage',
                    lazy: eagerLazy(
                      () =>
                        import('@/pages/settings-hub/_components/AdminSettingsHost'),
                      m => ({ Component: m.AdminSettingsHost }),
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'settings/integrations',
            element: (
              <Navigate to="../settings/personal#integrations" replace />
            ),
            title: 'Integrations',
          },
          {
            path: 'agents/:id',
            ...lazyLoadWithGuard(
              () => import('@/pages/agents/agent-detail'),
              undefined,
              undefined,
              { orgOnly: true },
            ),
            title: 'Agent',
          },
          {
            path: 'settings/agents',
            ...lazyLoadWithGuard(
              () => import('@/pages/settings/agents'),
              undefined,
              undefined,
              { orgOnly: true },
            ),
            title: 'Agents',
          },
          {
            path: 'members',
            element: <LegacyMembersRedirect />,
            title: 'Members',
          },
          // Team routes (requires subscription feature)
          // New primary routes
          {
            path: 'team',
            ...lazyLoadWithGuard(() => import('@/pages/team'), 'team'),
            title: 'Team',
          },
          {
            path: 'team/people',
            ...lazyLoadWithGuard(() => import('@/pages/team'), 'team'),
            title: 'Team people',
          },
          {
            path: 'team/departments',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team departments',
          },
          {
            path: 'team/planner',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team planner',
          },
          {
            // Team-IA Plan 05: Allocations folded into Planner as the List view.
            path: 'team/planner/list',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team planner',
          },
          {
            path: 'team/demand',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Demand',
          },
          {
            path: 'team/demand/:demandId',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Demand request',
          },
          {
            // Kept for the /team/insights -> /team/utilization redirect.
            path: 'team/insights',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team insights',
          },
          // Legacy /team money URLs: the money tabs moved to the
          // Profitability module at /financials (segment-preserving).
          {
            path: 'team/financials/*',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Profitability',
          },
          {
            path: 'team/forecast',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Profitability forecast',
          },
          {
            path: 'team/scenarios/*',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Profitability scenarios',
          },
          {
            path: 'team/rates/*',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Rates',
          },
          {
            path: 'team/earnings/*',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Earnings',
          },
          // Profitability module (internal name: financials): four tabs.
          // Overview (Performance stacked below it), Forecast (+ scenarios),
          // Billing (upcoming, monthly, earnings, contractor invoices) and
          // Setup (rates, approvals, budget settings).
          financialsRoute('financials', 'Profitability'),
          financialsRoute('financials/overview', 'Profitability Overview'),
          financialsRoute('financials/forecast', 'Profitability Forecast'),
          financialsRoute(
            'financials/forecast/scenarios',
            'Profitability Scenarios',
          ),
          financialsRoute(
            'financials/forecast/scenarios/:scenarioId',
            'Profitability Scenario',
          ),
          financialsRoute('financials/billing', 'Upcoming Billing'),
          financialsRoute('financials/billing/monthly', 'Monthly Billing'),
          financialsRoute('financials/billing/earnings', 'Earnings'),
          financialsRoute(
            'financials/billing/contractor-invoices',
            'Contractor Invoices',
          ),
          financialsRoute('financials/setup', 'Profitability Setup'),
          financialsRoute('financials/setup/rates', 'Rates'),
          financialsRoute('financials/setup/rates/overview', 'Rates Overview'),
          financialsRoute('financials/setup/rates/rates', 'Rates'),
          financialsRoute('financials/setup/rates/rate-cards', 'Rate Cards'),
          financialsRoute('financials/setup/rates/roles', 'Rates Roles'),
          financialsRoute(
            'financials/setup/rates/roles/:roleId',
            'Rates Role Detail',
          ),
          financialsRoute('financials/setup/approvals', 'Budget Approvals'),
          financialsRoute(
            'financials/setup/budget-categories',
            'Budget Categories',
          ),
          financialsRoute(
            'financials/setup/budget-templates',
            'Budget Templates',
          ),
          financialsRoute('financials/setup/overhead', 'Overhead'),
          // The in-module Settings address stays a real route: the Settings
          // shell and omni search deep-link to /financials/settings?section=,
          // and the page canonicalizes it onto /financials/setup/<section>.
          financialsRoute('financials/settings', 'Profitability Settings'),
          // Legacy ten-tab URLs (bookmarks, notifications, server hrefs).
          {
            path: 'financials/performance',
            element: <LegacyFinancialsRedirect />,
            title: 'Profitability performance',
          },
          {
            path: 'financials/scenarios/*',
            element: <LegacyFinancialsRedirect />,
            title: 'Profitability scenarios',
          },
          {
            path: 'financials/monthly',
            element: <LegacyFinancialsRedirect />,
            title: 'Monthly billing',
          },
          {
            path: 'financials/earnings/*',
            element: <LegacyFinancialsRedirect />,
            title: 'Earnings',
          },
          {
            path: 'financials/rates/*',
            element: <LegacyFinancialsRedirect />,
            title: 'Rates',
          },
          {
            path: 'financials/approvals',
            element: <LegacyFinancialsRedirect />,
            title: 'Budget approvals',
          },
          {
            path: 'team/settings',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team settings',
          },
          // Member detail redirect: old combined rates/compensation tab → Rates
          {
            path: 'team/people/:memberId/rates-and-compensation',
            element: <LegacyMemberRatesAndCompensationRedirect />,
          },
          // Person profile routes (new structure under /team/people/:memberId)
          {
            path: 'team/people/:memberId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/team/member-detail'),
              'team',
              'MemberDetail',
            ),
            title: 'Member details',
          },
          // Legacy redirects - old team tab routes to new equivalents
          {
            path: 'team/planning',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/planning/allocations',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/planning/schedule/:id',
            element: <PlanningScheduleRedirect />,
          },
          {
            path: 'team/planning/schedule',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/schedule',
            element: <PlannerRedirect />,
          },
          {
            path: 'team/allocations',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team allocations',
          },
          {
            // Team-IA Plan 05: Utilization is the renamed Insights tab; it now
            // loads the team page directly (the old insights redirect is gone).
            path: 'team/utilization',
            ...lazyLoadWithGuard(
              () => import('@/pages/team'),
              'team',
              undefined,
              { orgOnly: true },
            ),
            title: 'Team utilization',
          },
          {
            path: 'team/requests',
            element: <Navigate to="../team/demand" replace />,
          },
          {
            path: 'team/demand-requests',
            element: <Navigate to="../team/demand" replace />,
          },
          {
            path: 'team/compensation',
            element: <LegacyTeamMoneyRedirect />,
            title: 'Rates',
          },
          {
            path: 'team/roles',
            element: <LegacyTeamRoleRedirect />,
          },
          {
            path: 'team/roles/:roleId',
            element: <LegacyTeamRoleRedirect />,
          },
          {
            path: 'team/skills',
            element: <Navigate to="../team/people" replace />,
          },
          // Member detail redirects: planning + performance → workload (Plan 7b-2)
          {
            path: 'team/people/:memberId/planning',
            element: <Navigate to="../workload" relative="path" replace />,
          },
          {
            path: 'team/people/:memberId/performance',
            element: <Navigate to="../workload" relative="path" replace />,
          },
          // Legacy redirect - old member detail routes (/:memberId/* at team level)
          {
            path: 'team/:memberId/*',
            ...lazyLoadWithGuard(
              () => import('@/pages/team/member-detail-redirect'),
              'team',
              'MemberDetailRedirect',
              { orgOnly: true },
            ),
            title: 'Member details',
          },
          // Legacy redirect - redirect old resources path to team
          {
            path: 'resources',
            element: <Navigate to="../team" replace />,
          },
          {
            path: 'resources/*',
            element: <Navigate to="../team" replace />,
          },
          // Inventory routes — the module UI moved into Products; legacy URLs
          // redirect to their /products equivalents (item detail stays here)
          {
            path: 'inventory/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/inventory/redirect'),
              'INVENTORY',
            ),
            title: 'Inventory',
          },
          {
            path: 'inventory/item/:itemId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/inventory/item-detail'),
              'INVENTORY',
              'InventoryItemDetail',
            ),
            title: 'Inventory item details',
          },
          // Suppliers routes
          {
            path: 'suppliers/:supplierId/*',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers/supplier-detail'),
              'SUPPLIERS',
            ),
            title: 'Supplier details',
          },
          {
            path: 'suppliers',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Suppliers',
          },
          {
            path: 'suppliers/purchase-orders',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Purchase orders',
          },
          {
            path: 'suppliers/performance',
            // The SUPPLIERS flag like its sibling tabs; a subscription
            // guard alone ignored the flag and was open to personal spaces.
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Supplier performance',
          },
          {
            path: 'suppliers/analytics',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Supplier analytics',
          },
          {
            path: 'suppliers/settings',
            ...lazyLoadWithFeatureFlag(
              () => import('@/pages/suppliers'),
              'SUPPLIERS',
            ),
            title: 'Supplier settings',
          },
          // Legacy redirect - redirect old subscription path to admin subscription
          {
            path: 'subscription',
            element: <LegacySubscriptionRedirect />,
          },
          {
            path: 'subscription/*',
            element: <LegacySubscriptionRedirect />,
          },
          // An unknown page inside a workspace stays inside it: without this
          // the app-wide catch-all drew the 404 with no sidebar or header.
          {
            path: '*',
            ...lazyLoad(() => import('@/pages/not-found')),
            title: 'Page not found',
          },
        ],
      },
    ],
  },
]

/**
 * The org PAGE routes: the subtree below OrgRouteGuard, WITHOUT
 * ProtectedLayout/MainLayout above it. The canvas's in-process page embed
 * (EmbeddedPagePanel) mounts these in its own memory router so a
 * directive-opened page renders the real route components — chromeless by
 * construction — instead of iframing the whole app.
 *
 * Derived from the tree above rather than duplicated, so new pages are
 * embeddable the moment they are routed.
 */
export const orgPageRoutes: AppRouteObject[] =
  (organizationRoutes[0]?.children?.find(
    route =>
      isValidElement(route.element) && route.element.type === OrgRouteGuard,
    // The intersection type loses the narrowed children type on access.
  )?.children ?? []) as AppRouteObject[]
